import type { JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import type { SelfUser } from '@wishare/shared';
import { api, ApiError } from './api.js';

/**
 * Состояние авторизации.
 *
 * Единственный источник правды — ответ сервера на /auth/me. Мы намеренно не
 * храним пользователя в localStorage: там он рассинхронизируется с настоящей
 * сессией, и человек будет видеть себя вошедшим, получая ошибки на каждом шаге.
 */

interface AuthState {
  user: SelfUser | null;
  isLoading: boolean;
  /**
   * Спросить сервер не вышло — это НЕ то же самое, что «не вошёл».
   *
   * Разница видна человеку сразу: при «не вошёл» правильно показать форму
   * входа, а при «не смог спросить» — это ложь. Сессия жива, cookie на месте,
   * и человек, которого выкинуло на вход из-за одного провала связи, решает,
   * что приложение его разлогинило.
   */
  сетьНеОтветила: boolean;
  /** Спросить сервера заново — руками, по кнопке. */
  спроситьЗаново(): void;
  login(email: string, password: string): Promise<void>;
  register(input: { email: string; password: string; displayName: string; username?: string }): Promise<void>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.auth.me();
      } catch (error) {
        // 401 — это не сбой, а нормальное «пользователь не вошёл».
        if (error instanceof ApiError && error.isAuthError) return null;
        throw error;
      }
    },
    /*
     * Одну попытку повторяем сами, и только при обрыве связи.
     *
     * В мобильной сети провалившийся запрос — это чаще всего рябь, а не отказ:
     * второй уходит через секунду и доходит. Показывать из-за такой ряби экран
     * «нет связи» значило бы дёргать человека там, где приложение справится
     * само. А вот ошибку сервера повторять незачем — она не пройдёт и со
     * второго раза, и ждать ещё раз впустую человеку не за что.
     */
    retry: (сколькоРаз, ошибка) =>
      сколькоРаз < 1 && ошибка instanceof ApiError && ошибка.status === 0,
    retryDelay: 1_000,
    staleTime: 60_000,
  });

  /*
   * Связь вернулась — спрашиваем сами, не дожидаясь кнопки.
   *
   * Так это и выглядит в жизни: человек открыл приложение в метро, увидел
   * «не получилось связаться», поднялся на улицу — и телефон уже в сети.
   * Заставлять его в этот момент искать кнопку значит заставлять делать работу,
   * которую приложение может сделать само. Слушаем и возвращение сети,
   * и возвращение самого приложения на экран: на телефоне вкладку усыпляют,
   * и событие о сети может пройти мимо спящего приложения.
   */
  useEffect(() => {
    if (!isError) return undefined;

    const спросить = (): void => {
      void refetch();
    };
    const приПоказе = (): void => {
      if (document.visibilityState === 'visible') спросить();
    };

    window.addEventListener('online', спросить);
    document.addEventListener('visibilitychange', приПоказе);
    return () => {
      window.removeEventListener('online', спросить);
      document.removeEventListener('visibilitychange', приПоказе);
    };
  }, [isError, refetch]);

  const setUser = useCallback(
    (user: SelfUser | null) => {
      queryClient.setQueryData(['me'], user);
    },
    [queryClient],
  );

  const value = useMemo<AuthState>(
    () => ({
      user: data ?? null,
      isLoading,
      сетьНеОтветила: isError,
      спроситьЗаново() {
        void refetch();
      },

      async login(email, password) {
        setUser(await api.auth.login(email, password));
      },

      async register(input) {
        setUser(await api.auth.register(input));
      },

      async logout() {
        await api.auth.logout();
        setUser(null);
        // Данные предыдущего пользователя не должны мелькнуть у следующего.
        queryClient.clear();
      },

      async refresh() {
        await queryClient.invalidateQueries({ queryKey: ['me'] });
      },
    }),
    [data, isLoading, isError, refetch, queryClient, setUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth вызван вне AuthProvider');
  return context;
}
